/**
 * App.js — Main application shell with routing.
 * State/effects/handlers live in useMainApp hook.
 * Heavy UI sections extracted to MapTab & GymInfoModal.
 */
import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback, Suspense } from "react";
import { Routes, Route, useParams, Outlet, Navigate, useLocation, useNavigate } from "react-router";
import { auth, db } from "./firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { F, GRADE_SCALE, DEFAULT_ACCENT } from "./constants";
import { isConsentOutdated } from "./utils/legalConsent";
import { initials, avatarPhotoURL, isFeatureEnabled, currentHref } from "./utils";
import { historyFloor } from "./utils/historyFloor";
import { PUBLIC_DASHBOARD_RE } from "./utils/publicRoutes";
import { wallsForViewer } from "./utils/sprayWallTab";
import { sprayWallPrefetcher } from "./utils/sprayPhotoCache";
import { markSeen, getRegisteredTokenId, getPermissionState, refreshPushToken } from "./utils/pushNotifications";
import { recordPwaLaunch } from "./utils/pwaApp";
import { isPushDiscoveryPending } from "./utils/notifPrefs";
import { setPendingDestination } from "./utils/pendingDestination";
import { ThemeProvider, useAppTheme } from "./contexts/ThemeContext";
import { ToastProvider, SearchInput, TabBar, StatusBarTintBar, PublicNotFound } from "./components/ui";
import { statusBarInset } from "./utils/iosStatusBar";
import ProfileMenu from "./components/ProfileMenu";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { LoadingBlank } from "./components/LoadingSkeleton";
import { LoginCard } from "./components/LoginCard";
// Static, not lazyChunk: an outdated client's lazy chunks may be gone from Hosting.
import { UpdateNotices } from "./components/UpdateNotices";
import { useClientCompat } from "./hooks/useClientCompat";
import { useAccountLanguageRestore } from "./hooks/useAccountLanguageRestore";
import { useChunkRecoveryReport } from "./utils/chunkRecoveryReport";
import { useUpdateTraceReport } from "./utils/updateTraceReport";
import { localStore, sessionStore } from "./utils/safeStorage";
import { InstallBanner } from "./components/InstallBanner";
import { TopBannerHost } from "./components/TopBannerHost";
import { OfflineBanner } from "./components/OfflineBanner";
import { useMainApp } from "./hooks/useMainApp";
import { useHeaderCollapse, stickyTopPx } from "./hooks/useHeaderCollapse";
import { peekScrollMemory } from "./hooks/useScrollMemory";
import { useMeasuredHeight } from "./hooks/useMeasuredHeight";
import { usePullToRefresh, PullToRefreshIndicator } from "./hooks/usePullToRefresh";
import { holdsPlatformAccess, resolveGymCaps } from "@sendly/shared";
import { resolveRouteTapTarget } from "./competitionUtils";
import { version as appVersion } from "./buildInfo";
import { logEvent, EVT } from "./analytics";
import { MapTab } from "./components/MapTab";
// `lazyChunk`, not `React.lazy`: a chunk request that hangs (rather than fails)
// would otherwise park the root Suspense on its loader forever — the
// "boot-stall: suspense" white screen. See src/utils/lazyChunk.js.
import { lazyChunk } from "./utils/lazyChunk";
const RouteModal = lazyChunk(() => import("./components/RouteModal").then(m => ({ default: m.RouteModal })), "RouteModal");
const RouteBuilder = lazyChunk(() => import("./components/RouteModal").then(m => ({ default: m.RouteBuilder })), "RouteBuilder");
const StatsTab = lazyChunk(() => import("./components/StatsTab").then(m => ({ default: m.StatsTab })), "StatsTab");
const AdminPanel = lazyChunk(() => import("./components/AdminPanel").then(m => ({ default: m.AdminPanel })), "AdminPanel");
const SetterStats = lazyChunk(() => import("./components/SetterStats").then(m => ({ default: m.SetterStats })), "SetterStats");
const SprayWallTab = lazyChunk(() => import("./components/spraywall/SprayWallTab").then(m => ({ default: m.SprayWallTab })), "SprayWallTab");
import { GymSelector } from "./components/GymSelector";
import { WindowScrollFadeBottom } from "./components/ScrollFade";
const GymInfoModal = lazyChunk(() => import("./components/GymInfoModal").then(m => ({ default: m.GymInfoModal })), "GymInfoModal");
// Static, not lazyChunk: every boot renders it, so its own chunk saved nothing and
// put one more request under the root Suspense on the boot's critical path.
import { CookieConsent } from "./components/CookieConsent";
const ConsentModal = lazyChunk(() => import("./components/ConsentModal").then(m => ({ default: m.ConsentModal })), "ConsentModal");
const NameResetNoticeModal = lazyChunk(() => import("./components/NameResetNoticeModal"), "NameResetNoticeModal");
const TvDashboard = lazyChunk(() => import("./components/TvDashboard").then(m => ({ default: m.TvDashboard })), "TvDashboard");
const VersionPage = lazyChunk(() => import("./components/VersionPage").then(m => ({ default: m.VersionPage })), "VersionPage");
const LegalPage = lazyChunk(() => import("./components/LegalPage").then(m => ({ default: m.LegalPage })), "LegalPage");
const PlatformCalendarPage = lazyChunk(() => import("./components/PlatformCalendarPage").then(m => ({ default: m.PlatformCalendarPage })), "PlatformCalendarPage");
const PlatformAdmin = lazyChunk(() => import("./components/PlatformAdmin"), "PlatformAdmin");
const GlobalProfile = lazyChunk(() => import("./components/GlobalProfile"), "GlobalProfile");
import { useTranslation } from "./i18n";
const CompetitionTab = lazyChunk(() => import("./components/competition/CompetitionTab").then(m => ({ default: m.CompetitionTab })), "CompetitionTab");
const CompetitionDashboard = lazyChunk(() => import("./components/CompetitionDashboard").then(m => ({ default: m.CompetitionDashboard })), "CompetitionDashboard");
const CompetitionRegisterLanding = lazyChunk(() => import("./components/CompetitionRegisterLanding").then(m => ({ default: m.CompetitionRegisterLanding })), "CompetitionRegisterLanding");
const EventRegisterLanding = lazyChunk(() => import("./components/EventRegisterLanding").then(m => ({ default: m.EventRegisterLanding })), "EventRegisterLanding");
const SeriesDashboard = lazyChunk(() => import("./components/SeriesDashboard").then(m => ({ default: m.SeriesDashboard })), "SeriesDashboard");
const WrappedPage = lazyChunk(() => import("./components/WrappedPage"), "WrappedPage");
const RoutesTab = lazyChunk(() => import("./components/RoutesTab").then(m => ({ default: m.RoutesTab })), "RoutesTab");
const TvControl = lazyChunk(() => import("./pages/TvControl"), "TvControl");
const CompetitionAttemptModal = lazyChunk(() => import("./components/competition/CompetitionAttemptModal").then(m => ({ default: m.CompetitionAttemptModal })), "CompetitionAttemptModal");
const CompetitionAttemptEditModal = lazyChunk(() => import("./components/competition/CompetitionAttemptEditModal").then(m => ({ default: m.CompetitionAttemptEditModal })), "CompetitionAttemptEditModal");
const PublicWidget = lazyChunk(() => import("./components/PublicWidget").then(m => ({ default: m.PublicWidget })), "PublicWidget");
const BannedScreen = lazyChunk(() => import("./components/BannedScreen").then(m => ({ default: m.BannedScreen })), "BannedScreen");
const OAuthAuthorizePage = lazyChunk(() => import("./components/OAuthAuthorizePage").then(m => ({ default: m.OAuthAuthorizePage })), "OAuthAuthorizePage");
import { useGym } from "./contexts/GymContext";
import { useCurrentUser } from "./contexts/CurrentUserContext";
import { getPlatformConfig } from "./utils/platformConfig";
import { TodayClimbLogContext } from "./hooks/TodayClimbLogContext";
import { useBackgroundPrefetch } from "./hooks/useBackgroundPrefetch";
import { readPersistedPeriod } from "./hooks/usePersistedPeriod";
import { resolvePeriod, normalizePeriod } from "./utils/periodUtils";
import { clampRange } from "./utils/historyFloor";

// The code warm-up, per tab-bar key (docs/spec/01f-tab-loading.md, "Code
// warm-up from the tab bar"): only what the bar shows is warmed, so a viewer
// without an admin capability never downloads the admin UI. The map is in the
// main bundle; `RouteModal` is warmed for everyone.
//
// Every `.catch()` is load-bearing, not defensive noise. These warm chunks for
// components NOBODY IS RENDERING, so a failed one is invisible to the user —
// but an UNCAUGHT one becomes an `unhandledrejection`, and crashLogger's
// handler treats any chunk-shaped message as a stale chunk and fires the full
// recovery: unregister every service worker, purge Cache Storage, cache-busted
// reload — yanking the page out from under someone mid-session, the opposite
// of the user-controlled update rule (docs/guides/platform-invariants.md).
// They do not go through `lazyChunk` either, so nothing bounds them; a warm-up
// that hangs simply never settles. The ONLY consequence of a failed warm-up is
// that the chunk loads on demand later, through the deadline-guarded lazy path.
const WARM_CHUNKS = {
  routeModal: () => import("./components/RouteModal").catch(() => {}),
  spraywall: () => import("./components/spraywall/SprayWallTab").catch(() => {}),
  stats: () => import("./components/StatsTab").catch(() => {}),
  comp: () => import("./components/competition/CompetitionTab").catch(() => {}),
  routes: () => import("./components/RoutesTab").catch(() => {}),
  setters: () => import("./components/SetterStats").catch(() => {}),
  admin: () => import("./components/AdminPanel").catch(() => {}),
};

/** Reports the tab it rendered with once its lazy chunk is in (inside the content Suspense). */
function ViewMountMarker({ view, onMounted }) {
  useEffect(() => { onMounted(view); }, [view, onMounted]);
  return null;
}

/* ========== MAIN APP ========== */
// Exported for MainApp.smoke.test.js — the shell has no render coverage
// otherwise, and phase 4 moves state out of useMainApp underneath it.
export function MainApp() {
  // When the URL is /gym/:gymSlug/event/:eventId, react-router fills in
  // eventId here. MainApp passes it through useMainApp so the events section
  // can auto-focus + auto-open the corresponding card (also works for
  // unlisted events that don't appear in the normal list).
  const { eventId: initialEventId } = useParams();
  const app = useMainApp({ initialEventId });
  const [gradeScale, setGradeScale] = useState(GRADE_SCALE);
  const [compAttemptRoute, setCompAttemptRoute] = useState(null);
  const [compAttemptExisting, setCompAttemptExisting] = useState(null);
  // One-shot signal "jump to the map and focus this route" — set by either
  // the routes tab's target icon, or the map tab's own route-list target
  // icon. Consumed by ZoomableMap's focus useEffect (zooms in, flashes the
  // marker pulse, scrolls the SVG into view). Carries a timestamp so two
  // consecutive requests for the same route still trigger (object identity
  // changes even when the id repeats).
  const [mapFocusRequest, setMapFocusRequest] = useState(null);
  useEffect(() => {
    getPlatformConfig().then(cfg => {
      if (Array.isArray(cfg.gradeScale) && cfg.gradeScale.length > 0) setGradeScale(cfg.gradeScale);
    }).catch(() => {});
  }, []);

  // The pinned block is `position: fixed`, so its height has to be held by a
  // spacer — see the block itself for why it is not sticky. A callback ref, not
  // a mount-time observer: this shell renders a loading screen while auth
  // resolves, so the block does not exist yet on the first pass.
  const [stickyHeaderRef, headerHeight] = useMeasuredHeight();
  const {
    t, lang, navigate,
    authUser, authLoading, userData, seedName, handleLogin, handleAppleLogin, handleEmailLink, handleLogout,
    emailInput, setEmailInput, passwordInput, setPasswordInput,
    emailSent, setEmailSent,
    authError, setAuthError, needsEmailConfirm, handleConfirmEmail,
    isPasswordAuthEnabled, handlePasswordSignIn, handlePasswordSignUp,
    gymId, gymMeta, settings, accent, diff, holdC, th,
    planLimits, tabs,
    users, members, routes, ascents, allAscents, friendships,
    ascentCounts, fetchAllAscents,
    rankingAscents, fetchRankingAscents, rankingStatus,
    routeAscents, fetchAscentsForRoutes,
    routeRatings, fetchRatingsForRoutes,
    archivedRoutes, archivedRoutesHasMore, fetchArchivedRoutes, loadMoreArchivedRoutes, fetchAllArchivedRoutes,
    sprayWalls, sprayWallsLoaded, openSprayWall,
    ownMember, isBanned,
    acceptedFriendUids, pendingIncoming, friendsFilter, setFriendsFilter,
    isGymAdmin, can, stats,
    tab, setTab, selRoute, setSelRoute, selRouteEditMode, setSelRouteEditMode, leaderboardDiscipline, setLeaderboardDiscipline,
    showBuilder, setShowBuilder, pending, setPending,
    showGymInfo, setShowGymInfo,
    registerNavigationGuard,
    filters, setFilters, setterSearch, setSetterSearch,
    mapSearch, setMapSearch, mapSort, setMapSort,
    showMapFilters, setShowMapFilters,
    activeMapId, setActiveMapId, maps, currentMapId, currentMap,
    filteredRoutes, sortedRouteList, doneSet, compToppedRouteIds, activeFilterCount, toggleDiff, hasUngradedRoutes, hasUnsetFieldRoutes, compRouteIds, myScorecardRouteIds, compMapContext, canShowRouteOnMap, avgRatingMap,
    disciplineFilter, setDisciplineFilter,
    pullY, refreshing, PULL_THRESHOLD,
    isStandalone, isIOSStandalone, isIOS, isGymPwa,
    installPrompt, showInstallBanner, handleInstallPwa, dismissInstallBanner,
    logClimb, logAttempt, reduceAttempt, setSendAttempts, removeRouteDay, rewriteAsFlash, changeGradeVote, removeClimb, removeRouteClimbs, fetchRouteClimbs, routeLogCache, todayClimbLog, fetchClimbLogs, myGymStats, fetchRoutesArchivedSince,
    quickLog, archiveMode,
    handleRate,
    handleSaveRoute, handleMapClickOrReposition, handleEditRoute, handleDeleteRoute, handleRepositionRoute,
    handleRole, handleKick, handleBan, handleUnban, handleReportName,
    handleBulkArchive, handleBulkDelete, handlePurge,
    handleSettingsChange, handleGymMetaChange, handleUpload,
    handleMarkLegendSeen,
    handleSendFriendRequestByUid,
    compHook,
    seriesHook,
    eventsHook,
    adminSources, sprayPickerSources, compTabSources,
    dataLoaded, todayKey,
    scrollRestoring,
  } = app;

  // The date a non-exempt viewer's ascent history becomes visible in this gym
  // (their own `joinedAt` day, in the gym's timezone) — null for an admin/gymAdmin
  // (exempt) or a member predating joinedAt tracking. Own data is never limited by
  // this; it only clamps what a viewer sees of OTHER climbers. See
  // docs/spec/12-user-app.md "Hiding the history from before joining".
  // One check covers both exemptions: resolveGymCaps grants a
  // `platform.gym.adminAny` holder every gym cap, `stats.viewAdvanced` included,
  // while the gym-scoped `can()` never answers for a platform cap by name.
  const historyFloorExempt = !!(can && can('stats.viewAdvanced'));
  const gymHistoryFloor = useMemo(
    () => historyFloor({ member: ownMember, timezone: gymMeta?.timezone, exempt: historyFloorExempt }),
    [ownMember, gymMeta?.timezone, historyFloorExempt]
  );

  // The walls this viewer may see, for the Statistics tab (the spray journal and the
  // open projects tell a visible wall from a hidden or deleted one with it).
  const sprayWallsForStats = useMemo(
    () => wallsForViewer(sprayWalls, can("spraywall.manage") === true),
    [sprayWalls, can]
  );

  // ── Warm-up and background prefetch (docs/spec/01f-tab-loading.md) ──
  // Both wait for the visible view: the always-on data, the active tab's chunk
  // (ViewMountMarker below) and the source layer's own reads.
  const [mountedView, setMountedView] = useState(null);
  const tabKeys = useMemo(() => (tabs || []).map(tb => tb.key), [tabs]);
  // One-shot reads only, never a listener; a view the viewer cannot open has
  // no entry. Each request is the one the view itself will make on open.
  const prefetchers = useMemo(() => {
    const inBar = new Set(tabKeys);
    const advanced = isFeatureEnabled(gymMeta, "advancedStats");
    return {
      stats: () => {
        const reads = [];
        const stored = readPersistedPeriod("period.leaderboard", { type: "weekly", offset: 0 }, gymId);
        const range = clampRange(resolvePeriod(normalizePeriod(stored), todayKey), gymHistoryFloor);
        if (!range.empty && fetchRankingAscents) reads.push(fetchRankingAscents(range.from, range.to));
        // The timeline's 30-day own-log window, computed as StatsTab does.
        if (authUser && advanced && todayKey && fetchClimbLogs) {
          const cutoff = new Date(todayKey + "T00:00:00");
          cutoff.setDate(cutoff.getDate() - 30);
          reads.push(fetchClimbLogs(cutoff.toISOString().slice(0, 10), todayKey));
        }
        return Promise.all(reads);
      },
      // The picker counts (2+ walls: a one-wall gym opens the wall page and never
      // shows the picker), then the wall photos for the service worker cache (1+ walls).
      spraywall: sprayWallPrefetcher({
        inBar: inBar.has("spraywall"), walls: sprayWallsForStats, pickerSources: sprayPickerSources, gymId,
      }),
      setters: inBar.has("setters") && fetchAllArchivedRoutes ? () => fetchAllArchivedRoutes() : undefined,
      adminStats: inBar.has("admin") && can("stats.viewAdvanced") && adminSources?.prefetch
        ? () => adminSources.prefetch({ advanced, withMau: can("billing.view") === true }) : undefined,
    };
  }, [tabKeys, gymMeta, gymId, todayKey, gymHistoryFloor, authUser, fetchRankingAscents, fetchClimbLogs, sprayWallsForStats, sprayPickerSources, fetchAllArchivedRoutes, adminSources, can]);
  // The route modal is reachable from the map, the default view, so its read
  // does not wait for usage (01f, "The always-run prefetch").
  const alwaysPrefetch = useMemo(
    () => (authUser && routeLogCache ? [routeLogCache.prefetch] : []),
    [authUser, routeLogCache],
  );
  useBackgroundPrefetch({
    gymId, tab, tabKeys, dataLoaded, mountedView,
    warmChunks: WARM_CHUNKS, prefetchers, always: alwaysPrefetch,
  });

  // The pinned header + tabs fold and then slide out of view as the reader
  // travels down, and come back the same way going up — tied to the finger,
  // not switched by it. `tab` is the reset key: useScrollMemory restores each
  // tab's own offset on a switch, and that programmatic jump would otherwise
  // be spent as collapse budget right as the user arrives.
  // Frozen while the profile menu is open: its panel hangs off the chip in
  // this very block, and a header that slides out from under it leaves the
  // menu floating over the page, attached to nothing.
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const collapse = useHeaderCollapse({
    resetKey: tab,
    frozen: profileMenuOpen,
    isProgrammatic: () => scrollRestoring?.current?.active === true,
    // Read from storage, not from the restore ref: this is answered during the
    // render of the switch, and the ref is only filled a phase later.
    pendingScrollY: () => (gymId ? peekScrollMemory(tab, gymId) : null),
  });

  // The pinned block is fixed, so a sticky header of the page content (the
  // FullPageEditor cards) must stick under its visible edge, not behind it at
  // top: 0. Published as a CSS variable, rewritten before paint on every change
  // of the height or of the slide (spec 01 "Pinned header", --sendly-sticky-top).
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--sendly-sticky-top", `${stickyTopPx(headerHeight, collapse.shift, statusBarInset())}px`);
  }, [headerHeight, collapse.shift]);
  useLayoutEffect(() => () => { document.documentElement.style.removeProperty("--sendly-sticky-top"); }, []);

  // (Legal consent check moved to App level)

  // Which modal does a tap on the selected route belong to? Resolved DURING
  // RENDER so the gate below and the swap effect answer from the same verdict
  // in the same render — that is what keeps the ordinary RouteModal from
  // flashing on screen before a competition route becomes a scorecard.
  const routeTapTarget = resolveRouteTapTarget(
    selRoute, selRouteEditMode, compMapContext, myScorecardRouteIds, compHook.activeCompMyAttempts
  );

  // Swap a competition-owned selection over to the competition modal.
  // Synchronous, from the live `activeCompMyAttempts` listener — no await
  // between the tap and the modal. The previous `getDoc` round trip left the
  // tap hanging for as long as the network took, and on a stalled request
  // forever: the ordinary RouteModal stayed up and the scorecard never arrived.
  useEffect(() => {
    if (routeTapTarget !== "attempt" && routeTapTarget !== "judge") return;
    setCompAttemptExisting(routeTapTarget === "attempt"
      ? (compHook.activeCompMyAttempts?.find(a => a.routeId === selRoute.id) || null)
      : null);
    setCompAttemptRoute(selRoute);
    setSelRoute(null); setSelRouteEditMode(false); // Clear normal route modal
  }, [routeTapTarget, selRoute, compHook.activeCompMyAttempts, setSelRoute, setSelRouteEditMode]);

  // When the user opens / returns to the app, mark notifications seen — clears
  // them on this device (handled in index.html) and best-effort on the user's
  // OTHER devices via the markNotificationsSeen callable (only fires a clear
  // push when there are unseen notifications). allowWithoutToken lets a
  // non-push-registered device (e.g. the user's desktop) still trigger the
  // cross-device clear; gated on having received a push before so the callable
  // doesn't run on every focus for users who never use push.
  //
  // CRITICAL: `pushMeta.lastNotifiedAt` is read via a ref, NOT an effect dep.
  // If it were a dep, the effect would re-run when a push ARRIVES and the
  // immediate onVisible() would fire markSeen — prematurely advancing lastSeenAt
  // on a backgrounded-but-visible device, consuming the "unseen" signal before
  // the user actually opens the app on another device, so the cross-device
  // clear never fires. markSeen must only run on a real focus/visibility event.
  //
  // The mount call therefore has to WAIT for the user doc: the effect keys on
  // authUser.uid, which resolves before the users/{uid} snapshot, so at mount
  // `userData` is still null and the allowWithoutToken gate reads false. On a
  // desktop (no push token of its own) that made the call a no-op, and since a
  // tab that stays focused fires no further visibility/focus event, the whole
  // session then went silent — reloading the page only repeated the same
  // no-op. The second effect below replays the mount call once the doc lands.
  const userDataRef = useRef(userData);
  userDataRef.current = userData;
  const runMarkSeenRef = useRef(null);
  const userDataLoaded = !!userData;
  useEffect(() => {
    if (!authUser?.uid) return;
    let last = 0;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      // No user doc yet → the allowWithoutToken gate cannot be evaluated.
      // Return WITHOUT taking the throttle slot, or the replay below (and any
      // focus event in the next 30s) would be swallowed by a call that did
      // nothing. A device with its own push token never needs the gate, so it
      // still calls immediately — exactly as before.
      if (!userDataRef.current && !getRegisteredTokenId()) return;
      const now = Date.now();
      if (now - last < 30000) return; // throttle rapid visibility toggles
      last = now;
      markSeen({ allowWithoutToken: !!userDataRef.current?.pushMeta?.lastNotifiedAt });
    };
    runMarkSeenRef.current = onVisible;
    onVisible();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      runMarkSeenRef.current = null;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [authUser?.uid]);

  // Replay the mount call when the user doc arrives. Keyed on the BOOLEAN
  // "loaded" — not on pushMeta — so it fires once per load and never re-runs
  // on a push arrival, which is the trap the comment above describes.
  useEffect(() => {
    if (userDataLoaded) runMarkSeenRef.current?.();
  }, [userDataLoaded]);

  // Push self-heal on boot: a device that claims to be push-registered
  // (localStorage token id) can silently go deaf — the FCM token rotates, a
  // send fails, and the server prunes the token doc while the UI still says
  // "enabled". Re-fetch the token and rewrite its doc once per boot so the
  // device heals without the user having to open Settings → Notifications
  // (which was previously the only place this ran). Deferred so the
  // firebase/messaging chunk never competes with first paint; gated on an
  // existing registration so it can never prompt or re-subscribe a device
  // where the user turned push off.
  useEffect(() => {
    if (!authUser?.uid) return;
    const uid = authUser.uid;
    const id = setTimeout(() => {
      if (getRegisteredTokenId() && getPermissionState() === "granted") {
        refreshPushToken(uid).catch(() => {});
      }
    }, 8000);
    return () => clearTimeout(id);
  }, [authUser?.uid]);

  // Shared "show this route on the map" handler — fires from the routes
  // tab's target icon AND the map tab's own list target icon. Switches
  // tab / floor as needed and posts a timestamped focus request that
  // ZoomableMap's effect consumes (zooms + flashes the marker). The
  // setTimeout cleanup clears the request after consumption so a later
  // unmount/remount (e.g. tab pingpong) doesn't re-fire on stale state.
  // Declared BEFORE any early-return below — Rules of Hooks: useCallback
  // must run on every render or React throws "rendered fewer hooks than
  // expected" when authLoading flips from true → false.
  const handleShowOnMap = useCallback((r) => {
    if (!r || r.x == null || r.y == null) return;
    setTab("map");
    const targetMap = r.mapId || "default";
    if (targetMap !== activeMapId) setActiveMapId(targetMap);
    setMapFocusRequest({ id: r.id, ts: Date.now() });
    setTimeout(() => setMapFocusRequest(null), 250);
  }, [activeMapId, setTab, setActiveMapId]);

  // Deep link: `?route=<routeId>` (from a watched-route push) opens the map
  // with the marker focused + pulsed. Runs once routes have loaded so we can
  // resolve the route's map/coords; strips the param afterwards so a refresh
  // doesn't re-fire. If the route is gone (archived → not in the active-only
  // list) we still land on the map tab.
  const routeFocusDoneRef = useRef(false);
  useEffect(() => {
    if (routeFocusDoneRef.current) return;
    const rid = new URLSearchParams(window.location.search).get("route");
    if (!rid) { routeFocusDoneRef.current = true; return; }
    if (!routes || routes.length === 0) return; // wait for the routes listener
    routeFocusDoneRef.current = true;
    const url = new URL(window.location);
    url.searchParams.delete("route");
    window.history.replaceState({}, "", url.pathname + url.hash);
    const r = routes.find(x => x.id === rid);
    if (r && r.x != null && r.y != null) handleShowOnMap(r);
    else setTab("map");
  }, [routes, handleShowOnMap, setTab]);

  // (The boot-stall watchdog's "we booted" signal used to be set here, on auth
  // resolution. It now lives in <LoadingBlank>'s unmount — MainApp mounts on the
  // gym routes only, so every other route reported healthy boots as hangs. See
  // the comment in components/LoadingSkeleton.js.)

  // ── Sign-in hand-back ──
  // A public page can send a signed-out visitor here purely to authenticate —
  // both sign-up landings (competition and event) do, since the login card lives at
  // `/gym/:slug` and nowhere else. `?returnTo=` names where to put them once
  // they are in, so they resume reading the page they came from instead of
  // being dumped in the app and having to find their way back.
  //
  // Only same-origin app paths are honoured. A `returnTo` arrives in a URL
  // anyone can craft and hand to a user, so an unvalidated one is an open
  // redirect: the pattern rejects absolute URLs and protocol-relative `//host`
  // by requiring a single leading slash followed by a known route root.
  useEffect(() => {
    if (!authUser) return;
    const target = new URLSearchParams(window.location.search).get("returnTo");
    const safe = /^\/(gym|calendar|profile)\/?[^/]/.test(target)
      && !target.startsWith("//")   // protocol-relative `//host` is an off-site jump
      && !target.includes("..");    // and `..` climbs back out of the allowed roots
    if (!target || !safe) return;
    navigate(target, { replace: true });
  }, [authUser, navigate]);

  // ── Early returns ──

  if (authLoading) return <LoadingBlank stage="auth" />;

  // Block inactive gyms for non-admin users (platform admins and gym admins can still access)
  const isPlatformAdmin = can('platform.gym.adminAny');
  if (gymMeta && gymMeta.isActive === false && !isPlatformAdmin && !isGymAdmin) return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: F, background: th.bg }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🏔️</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: th.text, marginBottom: 8 }}>{t('gymSelector.notFound')}</div>
      <a href="/" style={{ color: th.text, fontWeight: 600 }}>{t('gymSelector.backToList')}</a>
    </div>
  );

  if (!authUser) return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: th.bg, padding: 20, fontFamily: F }}>
      {gymMeta?.logoUrl ? <img src={gymMeta.logoUrl} alt="logo" style={{ height: 60, marginBottom: 12 }} /> :
        <div style={{ fontSize: 28, fontWeight: 900, color: accent, marginBottom: 8 }}>{gymMeta?.name || "Sendly"}</div>}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 40, opacity: 0.7 }}>
        <svg width="16" height="16" viewBox="0 0 128 128" fill="none">
          <rect width="128" height="128" rx="28" fill={accent}/>
          <path d="M88 18 C88 18 42 18 42 44 C42 62 88 62 88 80 C88 106 42 106 42 106" stroke="white" strokeWidth="9" strokeLinecap="round" fill="none"/>
        </svg>
        <span style={{ fontSize: 12, color: th.textMuted, letterSpacing: 3, fontWeight: 600 }}>SENDLY</span>
      </div>
      <LoginCard

        auth={{
          emailInput, setEmailInput, passwordInput, setPasswordInput,
          emailSent, setEmailSent, authError, setAuthError,
          needsEmailConfirm, handleConfirmEmail,
          isStandalone, isIOSStandalone, isPasswordAuthEnabled,
          handleLogin, handleAppleLogin, handleEmailLink,
          handlePasswordSignIn, handlePasswordSignUp,
        }}
      />
      <div style={{ fontSize: 12, color: th.textMuted, textAlign: "center", padding: "24px 0 8px", letterSpacing: 1 }}>© 2026 Sendly</div>
    </div>
  );

  // Banned from this gym — full-page banner, no gym data access.
  if (isBanned) return (
    <Suspense fallback={<LoadingBlank />}>
      <BannedScreen
        gymMeta={gymMeta}
        ownMember={ownMember}
       
        lang={lang}
        onBack={() => navigate("/")}
      />
    </Suspense>
  );

  // ── Authenticated render ──

  return (
    <div style={{ minHeight: "100vh", background: th.bg, fontFamily: F, position: "relative" }}>
      <StatusBarTintBar />

      {/* Pull-to-refresh indicator */}
      <PullToRefreshIndicator pullY={pullY} refreshing={refreshing} PULL_THRESHOLD={PULL_THRESHOLD} />

      {/* The pinned block below is out of the flow, so its space is kept here. */}
      <div aria-hidden="true" style={{ height: headerHeight }} />

      {/* PWA install banner — a pinned card in the shared top strip, not an
          in-flow strip: see the component header for why it moved. */}
      <InstallBanner
        show={showInstallBanner && !isStandalone && isFeatureEnabled(gymMeta, "gymPwa")}
        gymName={gymMeta?.name}
        installPrompt={installPrompt}
        isIOS={isIOS}
        onInstall={handleInstallPwa}
        onDismiss={dismissInstallBanner}
      />

      {/* HEADER + TAB BAR — single sticky block so tabs never slip under the
          header. The block also owns the iOS status-bar strip (statusBarInset
          + headerBg): in an iPad PWA the web view runs under the status bar, so
          without this the page content shows through behind the clock. */}
      {/* In an iPad PWA the status-bar strip has to stay painted on its own:
          the header slides out from under it, and without this the page shows
          through behind the clock. 0-height everywhere else. */}
      {statusBarInset() > 0 && (
        <div aria-hidden="true" style={{ position: "fixed", top: 0, left: 0, right: 0, height: statusBarInset(), background: th.headerBg, zIndex: 51 }} />
      )}
      <div ref={stickyHeaderRef} data-sticky-header style={{
        // FIXED, not sticky, and the reason is measured rather than assumed. A
        // sticky element is bounded by its containing block, so the moment the
        // window sits at an offset the document cannot support the block is
        // pushed off the top of the screen with the bottom of that block. A tab
        // switch does exactly that: it can shorten the page by thousands of
        // pixels in one commit while the viewport is still parked deep, and the
        // browser does not necessarily clamp the offset in the same frame.
        // Caught on an iPhone leaving the routes tab (parked at 835px, document
        // 4696px) for the setters tab (document 754px):
        //
        //    ms      y   docH   top    h  shift  lbl
        //     1     40    754     0  130   0.00  1.00
        //    21    835    754  -795  130   0.00  1.00   <- off screen
        //    34     40    754     0  130   0.00  1.00
        //
        // One frame with the whole header 795px above the viewport, gone and
        // back. Everything we own — shift, labelOpen, the height — is identical
        // in all three rows, which is why several fixes aimed at our own state
        // could not touch it. Fixed has no containing-block constraint, so a
        // short document cannot push it anywhere.
        position: "fixed", top: 0, left: 0, right: 0,
        zIndex: 50, paddingTop: statusBarInset(), background: th.headerBg,
        // Moves by transform, so nothing below reflows and the four surfaces
        // that measure this block still read its real height. No transition:
        // the offset already follows the finger, and easing it would only make
        // the block lag behind the page it is supposed to be part of.
        // `none`, not `translateY(0)`, at rest: an identity transform still
        // promotes the block to its own compositor layer for good, and buys
        // nothing. While a transform IS live the block becomes the containing
        // block for any `position: fixed` inside it — see ProfileMenu.
        transform: collapse.shift > 0 ? `translateY(${-collapse.shift * 100}%)` : "none",
      }}>
      {/* A 35%-opacity accent hairline: the full-strength line moved to the
          bottom of the tab bar, and without something here the gym name and
          the tabs run together. Tinted rather than neutral so the brand colour
          stays under the header, and faint so it cannot compete with the line
          below it — the emphasis belongs to the selection. */}
      <div style={{ background: th.headerBg, padding: "10px 16px", borderBottom: "1px solid " + accent + "59" }}>
        <div style={{ maxWidth: 960, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Native buttons: both were clickable spans a keyboard could not
                reach. The gym's name (or its logo, named by the gym) opens the
                gym info modal. */}
            {/* Named for where it goes: a plain „← Vissza" was the same name as
                every in-page back button below it (the full-page editor's). */}
            {!isGymPwa && <button type="button" aria-label={t('gymInfo.backToGyms')} title={t('gymInfo.backToGyms')} onClick={() => { window.location.href = "/"; }} style={{ cursor: "pointer", fontSize: 20, color: th.textMuted, lineHeight: 1, background: "none", border: "none", padding: 0, fontFamily: F }}>←</button>}
            <button type="button" onClick={() => setShowGymInfo(true)} title={t('gymInfo.title')} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", padding: 0, fontFamily: F }}>
              {gymMeta?.logoUrl ? <img src={gymMeta.logoUrl} alt={gymMeta?.name || ""} style={{ height: 42 }} /> :
                <div style={{ fontSize: 18, fontWeight: 900, color: accent }}>{gymMeta?.name || "Sendly"}</div>}
            </button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {stats && <div style={{ textAlign: "right" }}><div style={{ fontSize: 14, fontWeight: 800, color: accent }}>{stats.pts} {t('common.points')}</div><div style={{ fontSize: 12, color: th.textMuted }}>{stats.cnt} {t('common.routes')}</div></div>}
            <ProfileMenu
              onOpenChange={setProfileMenuOpen}
              font={F}
              avatarUrl={avatarPhotoURL(authUser, userData)}
              initials={initials(userData?.name || seedName || authUser.displayName)}
              name={userData?.name || seedName || authUser.displayName}
              notifCount={(isFeatureEnabled(gymMeta, "friends") ? pendingIncoming.length : 0) + (isPushDiscoveryPending(userData) ? 1 : 0)}
              items={[
                // One entry for the whole profile — stats AND settings live
                // there as tabs now. Carries the notification badge so the
                // signal chain (chip → this row → Settings tab → the section)
                // stays unbroken.
                // `from` lets the profile's ← arrow return to this exact view
                // (gym + tab), instead of dumping the user on the gym list.
                { key: "profile", icon: "👤", label: t('profile.menuMyProfile'), badge: true, onClick: () => navigate(isGymPwa ? `/profile?gym=${gymMeta?.slug || ""}` : "/profile", { state: { from: currentHref() } }) },
                ...(isPlatformAdmin ? [{
                  key: "admin", icon: "🛡️", label: t('gymSelector.menuPlatformAdmin'), href: "/admin",
                  // Ctrl/Cmd/Shift+click → let the browser open /admin in a new tab.
                  // Plain click stays in-app via the router (no full reload).
                  onClick: (e) => { if (e.metaKey || e.ctrlKey || e.shiftKey) return; e.preventDefault(); navigate("/admin"); },
                }] : []),
                { key: "help", icon: "📖", label: t('gymSelector.menuHelp'), href: "https://sendly.rocks/docs/intro", target: "_blank", rel: "noopener noreferrer" },
                { key: "feedback", icon: "💬", label: t('gymSelector.menuFeedback'), href: `mailto:support@sendly.rocks?subject=${encodeURIComponent(t('gymSelector.feedbackSubject', { version: appVersion }))}&body=${encodeURIComponent(t('gymSelector.feedbackBody', { version: appVersion }))}` },
                // Gym-specific reports ("this route isn't on the map") belong to
                // the gym's own inbox, not to Sendly support. Hidden without a
                // published address — an empty mailto: recipient opens a draft
                // that goes nowhere — and hidden in demo gyms, where nobody
                // reads that mailbox.
                ...(gymMeta?.isDemo !== true && gymMeta?.contactEmail?.trim() ? [{
                  key: "contactGym", icon: "✉️", label: t('gymSelector.menuContactGym'),
                  href: `mailto:${gymMeta.contactEmail.trim()}?subject=${encodeURIComponent(t('gymSelector.contactGymSubject', { gym: gymMeta.name || "" }))}&body=${encodeURIComponent(t('gymSelector.contactGymBody'))}`,
                  onClick: () => logEvent(EVT.CONTACT_GYM, { gym_id: gymId }),
                }] : []),
                { key: "logout", icon: "🚪", label: t('gymSelector.menuLogout'), muted: true, onClick: async () => {
                  await handleLogout();
                  // Leave the gym after signing out — but only in the normal app.
                  // A gym-scoped PWA has no selector to return to (start_url is this
                  // gym), so it stays put and shows the gym login screen instead.
                  if (!isGymPwa) window.location.href = "/";
                } },
              ]}
            />
          </div>
        </div>
      </div>

      {/* TAB BAR */}
      <TabBar
        tabs={tabs} active={tab} onSelect={setTab}
 labelOpen={collapse.labelOpen}
        ariaLabel={t('common.mainNavigation') || 'Main navigation'}
      />
      {/* MAP SEARCH BAR — inside sticky block so it never scrolls away */}
      {tab === "map" && (
        // Two divs on purpose: the page colour has to run the FULL width, while
        // the row itself stays on the 960px grid. Both on one element left a
        // header-coloured strip down each side of the search field on any
        // display wider than 960px.
        <div style={{ background: th.bg }}>
        <div style={{ padding: "10px 12px", display: "flex", gap: 8, alignItems: "center", maxWidth: 960, margin: "0 auto", boxSizing: "border-box" }}>
          <SearchInput
            value={mapSearch} onChange={setMapSearch}
            placeholder={t('map.searchPlaceholder')}
            ariaLabel={t('map.searchRoutes')}
            style={{ flex: 1 }}
          />
          <button aria-label={t('common.toggleFilters')} onClick={() => setShowMapFilters(true)} style={{
            width: 44, height: 44, borderRadius: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", position: "relative",
            background: activeFilterCount > 0 ? accent : th.card,
            border: "1px solid " + (activeFilterCount > 0 ? accent : th.border),
            color: activeFilterCount > 0 ? "#fff" : th.textMuted, flexShrink: 0,
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
            {activeFilterCount > 0 && (
              <div style={{ position: "absolute", top: -4, right: -4, minWidth: 18, height: 18, borderRadius: 999, background: "#dc2626", color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px" }}>{activeFilterCount}</div>
            )}
          </button>
        </div>
        </div>
      )}
      </div>{/* end pinned header+tabs */}

      {/* CONTENT — local Suspense so lazy tab chunks don't flash the full-page fallback */}
      <Suspense fallback={null}>
      <main style={{ maxWidth: tab === "admin" ? 1200 : 960, margin: "0 auto", padding: tab === "map" ? 0 : 10, paddingTop: tab === "map" ? 0 : 10 }}>

        {tab === "map" && <MapTab
 diff={diff} holdC={holdC}
          routes={routes} filteredRoutes={filteredRoutes}
          sortedRouteList={sortedRouteList} doneSet={doneSet} compToppedRouteIds={compToppedRouteIds} avgRatingMap={avgRatingMap}
          maps={maps} currentMapId={currentMapId} currentMap={currentMap}
          activeMapId={activeMapId} setActiveMapId={setActiveMapId}
          mapSearch={mapSearch} setMapSearch={setMapSearch}
          filters={filters} setFilters={setFilters}
          showMapFilters={showMapFilters} setShowMapFilters={setShowMapFilters}
          activeFilterCount={activeFilterCount} toggleDiff={toggleDiff} hasUngradedRoutes={hasUngradedRoutes} hasUnsetFieldRoutes={hasUnsetFieldRoutes}
          mapSort={mapSort} setMapSort={setMapSort}
          canCreateRoute={can('routes.create')} pending={pending} setPending={setPending} setShowBuilder={setShowBuilder}
          quickLog={quickLog} archiveMode={archiveMode} canArchive={can('routes.archive')} todayClimbLog={todayClimbLog}
          setSelRoute={setSelRoute} onRouteLongPress={can('routes.edit') ? (r) => { setSelRouteEditMode(true); setSelRoute(r); } : undefined} handleMapClickOrReposition={handleMapClickOrReposition}
          hasActiveComp={!!compRouteIds}
          gymMeta={gymMeta} disciplineFilter={disciplineFilter} setDisciplineFilter={setDisciplineFilter}
          userData={userData} onMarkLegendSeen={handleMarkLegendSeen}
          settings={settings}
          mapFocusRequest={mapFocusRequest}
          onShowOnMap={handleShowOnMap}
          compRouteIds={compRouteIds}
          sprayWalls={sprayWalls} onSprayWallClick={openSprayWall} canManageSprayWall={can('spraywall.manage')}
        />}

        {tab === "spraywall" && <TodayClimbLogContext.Provider value={todayClimbLog}><SprayWallTab
          gymId={gymId} gymMeta={gymMeta} settings={settings}
          sprayWalls={sprayWalls} sprayWallsLoaded={sprayWallsLoaded}
          authUser={authUser} can={can}
          maps={maps} setActiveMapId={setActiveMapId} setPending={setPending} setTab={setTab}
          registerNavigationGuard={registerNavigationGuard}
          gradePreference={userData?.gradePreference || "font"}
          fetchClimbLogs={fetchClimbLogs}
          historyFloor={gymHistoryFloor}
          routes={routes} users={users} members={members} difficulty={diff} holdColors={holdC}
          sprayPickerSources={sprayPickerSources}
        /></TodayClimbLogContext.Provider>}

        {tab === "stats" && <StatsTab
          users={users} members={members} ascents={ascents} routes={routes} difficulty={diff} holdColors={holdC}
 can={can}
          fetchClimbLogs={fetchClimbLogs} todayClimbLog={todayClimbLog} myGymStats={myGymStats}
          fetchRoutesArchivedSince={fetchRoutesArchivedSince}
          onOpenSprayProblem={openSprayWall} sprayWalls={sprayWallsForStats}
          authUser={authUser} userData={userData} gymMeta={gymMeta}
          leaderboardDiscipline={leaderboardDiscipline} setLeaderboardDiscipline={setLeaderboardDiscipline}
          friendsFilter={friendsFilter} setFriendsFilter={setFriendsFilter}
          acceptedFriendUids={acceptedFriendUids} friendships={friendships}
          onSendFriendRequest={handleSendFriendRequestByUid}
          gradePreference={userData?.gradePreference || "font"}
          rankingAscents={rankingAscents} fetchRankingAscents={fetchRankingAscents} rankingStatus={rankingStatus}
          historyFloor={gymHistoryFloor}
        />}

        {tab === "routes" && can('routes.viewTab') && <RoutesTab
          routes={routes} ascentCounts={ascentCounts} users={users} members={members}
          routeRatings={routeRatings} fetchRatingsForRoutes={fetchRatingsForRoutes}
          gradePreference={userData?.gradePreference || "font"}
          routeAscents={routeAscents} fetchAscentsForRoutes={fetchAscentsForRoutes}
          archivedRoutes={archivedRoutes} fetchArchivedRoutes={fetchArchivedRoutes} archivedRoutesHasMore={archivedRoutesHasMore} loadMoreArchivedRoutes={loadMoreArchivedRoutes}
          diff={diff} holdC={holdC} can={can} gymMeta={gymMeta}
          historyFloor={gymHistoryFloor}
          filters={filters} setFilters={setFilters} hasUngradedRoutes={hasUngradedRoutes}
          setterSearch={setterSearch} setSetterSearch={setSetterSearch}
          setSelRoute={setSelRoute} onEditRoute={(r) => { setSelRouteEditMode(true); setSelRoute(r); }}
          onShowOnMap={handleShowOnMap}
          canShowRouteOnMap={canShowRouteOnMap}
          compRouteIds={compRouteIds}
          gymId={gymId} watcherUid={resolveGymCaps(ownMember, null).has('routes.viewTab') ? (authUser?.uid || null) : null}
        />}

        {tab === "comp" && (
          (isFeatureEnabled(gymMeta, "competitions") || isFeatureEnabled(gymMeta, "events"))
            ? <React.Suspense fallback={<div style={{ textAlign: "center", padding: 40, color: th.textMuted }}>{t('common.loading')}</div>}>
                <CompetitionTab
                  authUser={authUser} userData={userData}
                  routes={routes} users={users} members={members}
 gymMeta={gymMeta}
                  difficulty={diff} holdColors={holdC}
                  compHook={compHook}
                  seriesHook={seriesHook}
                  eventsHook={eventsHook}
                  initialEventId={initialEventId}
                  acceptedFriendUids={acceptedFriendUids}
                  onBulkArchive={handleBulkArchive}
                  onShowOnMap={handleShowOnMap}
                  canShowRouteOnMap={canShowRouteOnMap}
                  compTabSources={compTabSources}
                />
              </React.Suspense>
            : <div style={{ textAlign: "center", padding: 40, color: th.textMuted }}>{t('common.featureNotAvailable')}</div>
        )}

        {tab === "setters" && can('stats.viewSetterTab') && <div>
          <SetterStats users={users} routes={routes} archivedRoutes={archivedRoutes} fetchAllArchivedRoutes={fetchAllArchivedRoutes} routeRatings={routeRatings} fetchRatingsForRoutes={fetchRatingsForRoutes} difficulty={diff} holdColors={holdC} gymMeta={gymMeta} onBulkArchive={handleBulkArchive} onBulkDelete={handleBulkDelete} onPurge={handlePurge} onOpenRoute={(r) => { setSelRouteEditMode(false); setSelRoute(r); }} />
        </div>}

        {tab === "admin" && can('adminPanel.access') && <div>
          <AdminPanel users={users} members={members} currentUser={userData} authUser={authUser} userData={userData} onRole={handleRole} onKick={handleKick} onBan={handleBan} onUnban={handleUnban} onReportName={handleReportName}
            settings={settings} onSettingsChange={handleSettingsChange} onUpload={handleUpload}
            gymMeta={gymMeta} onGymMetaChange={handleGymMetaChange} planLimits={planLimits}
 routes={routes} allAscents={allAscents} difficulty={diff} holdColors={holdC} onBulkArchive={handleBulkArchive} onBulkDelete={handleBulkDelete} onPurge={handlePurge} gradeScale={gradeScale} gradePreference={userData?.gradePreference || "font"} compHook={compHook} seriesHook={seriesHook} eventsHook={eventsHook} fetchAllAscents={fetchAllAscents} adminSources={adminSources} onTabChange={setTab} onShowOnMap={handleShowOnMap} compRouteIds={compRouteIds} />
        </div>}
        {/* Inside the content Suspense: it commits only once the active tab's
            lazy chunk is in, which is half of "the visible view is ready". */}
        <ViewMountMarker view={tab} onMounted={setMountedView} />
      </main>

      {/* data-app-footer: the Spraywall problem editor measures it, so its photo
          leaves room for this block and the page has nothing left to scroll. */}
      <div data-app-footer style={{ textAlign: "center", padding: "16px 0 8px" }}>
        <button type="button" onClick={() => setShowGymInfo(true)} style={{ fontSize: 12, color: accent, cursor: "pointer", fontWeight: 600, fontFamily: F, background: "none", border: "none", padding: 0 }}>{t('gymInfo.title')}</button>
        <div style={{ fontSize: 12, color: th.textMuted, marginTop: 4, letterSpacing: 1 }}>© 2026 Sendly</div>
      </div>

      </Suspense>

      {/* Modals — each wrapped in its OWN Suspense so a first-time
          lazy chunk load doesn't unmount the main content. The
          earlier setup wrapped <main> AND every modal inside one
          big Suspense; when a user first opened a route modal after
          a page refresh, the modal lazy-suspended → null fallback
          replaced the entire main → page height collapsed → browser
          reset scroll to 0 → chunk loaded → main remounted at the
          top. With per-modal Suspense, only the (already invisible)
          modal slot suspends; the page underneath stays mounted and
          scroll position is preserved. */}
      <Suspense fallback={null}>
        {routeTapTarget === "route" && <RouteModal route={selRoute} onClose={() => { setSelRoute(null); setSelRouteEditMode(false); }} onLogClimb={logClimb} onLogAttempt={logAttempt} onReduceAttempt={reduceAttempt} onSetSendAttempts={setSendAttempts} onRemoveRouteDay={removeRouteDay} onRewriteAsFlash={rewriteAsFlash} onChangeGradeVote={changeGradeVote} onRemoveClimb={removeClimb} onRemoveRouteClimbs={removeRouteClimbs} onLoadRouteClimbs={fetchRouteClimbs} peekRouteClimbs={routeLogCache.peek} todayClimbLog={todayClimbLog} onRate={handleRate} onEdit={handleEditRoute} onDelete={handleDeleteRoute} onReposition={handleRepositionRoute} user={authUser} userData={userData} canEditRoute={can('routes.edit')} canArchiveRoute={can('routes.archive')} canDeleteRoute={can('routes.delete')} ascents={ascents} ascentCounts={ascentCounts} users={users} members={members} routes={routes} difficulty={diff} holdColors={holdC} gradePreference={userData?.gradePreference || "font"} canViewStats={can('routes.viewTab')} canWatchRoute={resolveGymCaps(ownMember, null).has('routes.viewTab')} canViewProfile={can('users.viewProfile')} onBulkArchive={handleBulkArchive} onBulkDelete={handleBulkDelete} onPurge={handlePurge} gymMeta={gymMeta} gymId={gymId} initialEdit={selRouteEditMode} historyFloor={gymHistoryFloor} />}
      </Suspense>
      <Suspense fallback={null}>
        {/* A judged group's competitor gets this modal too, read-only: the
            modal itself decides from the registration whether it may be
            edited (getSelfScoreState).

            `activeCompGroups` / `activeCompSessions`, NOT `groups` / `sessions`.
            The latter follow `activeCompId`: empty until the user opens the
            Competition tab at all, and pointing at another competition entirely
            once they browse a closed one. The modal reads the group mode and
            the session status out of these lists to decide whether it may be
            edited, so an empty or foreign list would silently render every
            map-opened scorecard read-only. */}
        {compAttemptRoute && compMapContext && compMapContext.role === "competitor" && (
          <CompetitionAttemptModal
            route={compAttemptRoute} competition={compMapContext.comp} myRegistration={compMapContext.myReg}
            existingAttempt={compAttemptExisting} groups={compHook.activeCompGroups} sessions={compHook.activeCompSessions}
            registrations={compHook.registrations} attempts={compHook.attempts} difficulty={diff} holdColors={holdC}
            compHook={compHook} authUser={authUser}
            onClose={() => { setCompAttemptRoute(null); setCompAttemptExisting(null); }}
          />
        )}
        {compAttemptRoute && compMapContext && compMapContext.role === "judge" && (
          <CompetitionAttemptEditModal
            competition={compMapContext.comp} groups={compHook.activeCompGroups} sessions={compHook.activeCompSessions}
            registrations={compHook.registrations} attempts={compHook.attempts} routes={routes}
 difficulty={diff} holdColors={holdC}
            compHook={compHook} authUser={authUser} users={users}
            onClose={() => { setCompAttemptRoute(null); setCompAttemptExisting(null); }}
          />
        )}
      </Suspense>
      <Suspense fallback={null}>
        {showBuilder && <RouteBuilder onSave={handleSaveRoute} onCancel={() => setShowBuilder(false)} difficulty={diff} holdColors={holdC} routes={routes} onBulkArchive={handleBulkArchive} onBulkDelete={handleBulkDelete} onPurge={handlePurge} gymMeta={gymMeta} />}
      </Suspense>
      <Suspense fallback={null}>
        {showGymInfo && gymMeta && <GymInfoModal gymMeta={gymMeta} onClose={() => setShowGymInfo(false)} />}
      </Suspense>
      {/* Global "more below" affordance for body scroll — fades in when
          there's content below the viewport, fades out at the bottom or
          when content fits without scrolling. Hint primarily for mobile
          where the platform scrollbar is overlay-only and auto-hides. */}
      <WindowScrollFadeBottom color={th.bg} />
    </div>
  );
}

/* ========== GYM SHELL ========== */
function GymShell() {
  // The provider resolves the slug of this route itself (GymContext): the
  // shell only renders the outcome. `gymId` is live in the same render as
  // the Outlet below, so gym-scoped children never mount with a null or
  // stale id.
  const { gymId, gymMeta, slugStatus, retrySlugResolve } = useGym();
  const { t } = useTranslation();
  const { th } = useAppTheme();

  if (slugStatus === "notFound") return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: F, background: th.bg }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🏔️</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: th.text, marginBottom: 8 }}>{t('gymSelector.notFound')}</div>
      <a href="/" style={{ color: th.text, fontWeight: 600 }}>{t('gymSelector.backToList')}</a>
    </div>
  );
  // Slug resolve gave up (wedged Firestore channel — see GymContext). Distinct
  // from "notFound": the gym probably exists, we just couldn't reach it, so the
  // user gets a retry instead of a wrong "gym not found".
  if (slugStatus === "unavailable") return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: F, background: th.bg, padding: 24, textAlign: "center" }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>📡</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: th.text, marginBottom: 8 }}>{t('gymSelector.loadFailed')}</div>
      <div style={{ fontSize: 13, color: th.textMuted, marginBottom: 20, maxWidth: 340 }}>{t('gymSelector.loadFailedBody')}</div>
      <button onClick={retrySlugResolve} style={{ fontFamily: F, fontSize: 14, fontWeight: 700, color: th.text, background: "none", padding: "10px 16px", borderRadius: 10, border: "1px solid " + th.border, cursor: "pointer", marginBottom: 12 }}>{t('common.retry')}</button>
      <a href="/" style={{ color: th.textMuted, fontSize: 13, fontWeight: 600 }}>{t('gymSelector.backToList')}</a>
    </div>
  );
  // Gym-scoped children build Firestore refs from `gymId` on their first
  // render (`collection(db, "gyms", null, …)` would throw), so they wait for it.
  if (!gymId) return <LoadingBlank stage="gym-resolve" />;

  // From here down the gym's own accent replaces the platform one. Only the
  // accent: the light/dark preference belongs to the visitor, not the gym.
  // `gymMeta` lands a render or two after `gymId`, so this starts on the
  // inherited accent and moves — which is why the provider reads the prop
  // every render instead of seeding state from it.
  return (
    <ThemeProvider accent={gymMeta?.accentColor}>
      <Outlet />
    </ThemeProvider>
  );
}

/* ========== NOT FOUND PAGE ==========
 * Catches any URL that doesn't match a registered route (typos, stale QR
 * codes, deleted gyms, etc). Shows a friendly "page not found" with a link
 * back to the gym selector instead of a blank screen. */
function NotFoundPage() {
  const { t } = useTranslation();
  return <PublicNotFound title={t('notFound.title')} body={t('notFound.body')} linkTo="/" linkLabel={t('notFound.cta')} />;
}

/* ========== PLATFORM ADMIN GUARD ========== */
function PlatformAdminGuard() {
  const { t } = useTranslation();
  const { th } = useAppTheme();
  // PTR mirrors the gesture wired up in MainApp + GymSelector — without it
  // the platform admin page silently ignored swipe-down on mobile / iPad,
  // which is the only place admins routinely view it.
  const { pullY, refreshing, PULL_THRESHOLD } = usePullToRefresh();
  // Seed from `auth.currentUser` so an already-signed-in user doesn't
  // see a one-frame loading flash on re-navigation — the same pattern
  // useAuth uses internally.
  const [authUser, setAuthUser] = useState(() => auth.currentUser);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(() => !auth.currentUser);
  // Once we've successfully loaded a userData blob with platform caps,
  // remember it. Transient `null` auth fires (Firebase Auth cross-tab
  // sync sometimes briefly emits null while a sibling tab refreshes
  // the token — see below) then don't bounce the user to access-denied
  // mid-session. Without this guard, ctrl-clicking the "Enter gym"
  // link on the platform admin (opens /gym/{slug} in a new tab) caused
  // the ORIGINAL tab to flip to the 🔒 screen, requiring a refresh.
  const hadAccessRef = useRef(false);

  useEffect(() => {
    let pendingDenyTimer = null;

    const unsub = onAuthStateChanged(auth, (user) => {
      // Any incoming auth fire cancels a pending "show access denied"
      // timer — the auth state has resettled before the grace period
      // expired, no need to deny.
      if (pendingDenyTimer) { clearTimeout(pendingDenyTimer); pendingDenyTimer = null; }

      if (!user) {
        if (hadAccessRef.current) {
          // We had a valid user before. Treat the null as transient
          // and keep showing the admin UI for a short grace period.
          // If `user` is still null after 2 s we fall back to denying
          // — that covers genuine sign-out + revocation. 2 s is enough
          // for any cross-tab sync hiccup to clear (Firebase's own
          // cross-tab handshake completes in well under a second).
          pendingDenyTimer = setTimeout(() => {
            setAuthUser(null);
            setLoading(false);
          }, 2000);
          return;
        }
        // Never had access in the first place — show the denial
        // screen immediately. Same behavior as before.
        setAuthUser(null);
        setLoading(false);
        return;
      }

      setAuthUser(user);
      getDoc(doc(db, "users", user.uid))
        .then((snap) => {
          const data = snap.exists() ? snap.data() : null;
          setUserData(data);
          if (data && holdsPlatformAccess(data)) {
            hadAccessRef.current = true;
          }
          setLoading(false);
        })
        .catch(() => {
          // Transient getDoc failure — keep the previous userData (if
          // any) and end the loading state. The next auth fire will
          // try again.
          setLoading(false);
        });
    });
    return () => {
      unsub();
      if (pendingDenyTimer) clearTimeout(pendingDenyTimer);
    };
  }, []);

  if (loading) return <LoadingBlank />;
  const hasPlatformAccess = holdsPlatformAccess(userData);
  // Only fall through to access-denied when we genuinely have no
  // platform user. If hadAccessRef is true and authUser is null, the
  // grace-period timer above is keeping us in the "loading" branch
  // visually until either auth re-resolves or the timer expires.
  if ((!authUser && !hadAccessRef.current) || !hasPlatformAccess) return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: F, background: th.bg }}>
      <div aria-hidden="true" style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
      <h1 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700, color: th.text }}>{t("platformAdmin.accessDenied")}</h1>
      <a href="/" style={{ color: th.text, fontWeight: 600 }}>{t("platformAdmin.backToGyms")}</a>
    </div>
  );
  return (
    <>
      <PullToRefreshIndicator pullY={pullY} refreshing={refreshing} PULL_THRESHOLD={PULL_THRESHOLD} />
      {/* Resolved theme from useTheme (localStorage `sendly-theme`, resolving
          "system") — NOT userData.theme, which is usually undefined here and
          fell back to "light", so the platform admin ignored the theme. */}
      <PlatformAdmin userData={userData} />
      {/* Same body-scroll affordance as the gym shell — desktop only,
          touch devices opt out via the WindowScrollFadeBottom internal
          media-query check. */}
      <WindowScrollFadeBottom color={th.bg} />
    </>
  );
}

/* ========== GLOBAL PROFILE GUARD ========== */
function GlobalProfileGuard() {
  // Seed from `auth.currentUser` so an already-signed-in user doesn't see a
  // one-frame loading flash on re-navigation — same pattern as
  // PlatformAdminGuard above and useAuth internally.
  const [authUser, setAuthUser] = useState(() => auth.currentUser);
  const [loading, setLoading] = useState(() => !auth.currentUser);
  // A transient `null` auth fire must not throw the user off this page. The
  // redirect below is `replace`, so it also erases /profile from history —
  // there is no going back from a wrong one. Firebase briefly emits null while
  // cross-tab sync settles (see PlatformAdminGuard, which got this same guard
  // after ctrl-clicking a link flipped the original tab to access-denied), and
  // production hit it here too: accepting a service-worker update with several
  // tabs open reloads them all at once, and this page bounced to the gym
  // selector on the first null.
  const hadUserRef = useRef(!!auth.currentUser);

  useEffect(() => {
    let pendingDenyTimer = null;

    const unsub = onAuthStateChanged(auth, (user) => {
      // Any incoming fire means auth has resettled — cancel a pending bounce.
      if (pendingDenyTimer) { clearTimeout(pendingDenyTimer); pendingDenyTimer = null; }

      if (!user) {
        if (hadUserRef.current) {
          // We had a real user. Hold the page for a short grace period; if the
          // null is still standing after 2s it is a genuine sign-out and the
          // redirect happens then. 2s clears any cross-tab handshake (Firebase's
          // own completes in well under a second).
          pendingDenyTimer = setTimeout(() => { setAuthUser(null); setLoading(false); }, 2000);
          return;
        }
        // Never had a user — a signed-out visitor typed /profile, or followed
        // the unsubscribe row of a recap / admin-report mail, which links
        // straight at `/profile#settings/emails`. Hand the destination to the
        // gym selector before bouncing: the redirect below is `replace`, so
        // without this the page they asked for is gone from history too and
        // they are left on the gym list with no sign-in prompt and no way back
        // to the setting they came for. The selector opens its login modal for
        // a waiting destination and lands them on it once they are in.
        setPendingDestination(
          window.location.pathname + window.location.search + window.location.hash,
        );
        setAuthUser(null);
        setLoading(false);
        return;
      }

      hadUserRef.current = true;
      setAuthUser(user);
      setLoading(false);
    });
    return () => {
      unsub();
      if (pendingDenyTimer) clearTimeout(pendingDenyTimer);
    };
  }, []);

  if (loading) return <LoadingBlank />;
  if (!authUser) return <Navigate to="/" replace />;
  return <GlobalProfile user={authUser} />;
}

/* ========== ROUTER WRAPPER ========== */
// PUBLIC_DASHBOARD_RE (public spectator routes — no analytics, no cookie
// consent) lives in utils/publicRoutes.js, held against the boot script's
// route classification by pathKind.test.js.

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const isPublicDashboard = PUBLIC_DASHBOARD_RE.test(location.pathname);

  // Client–backend compatibility (docs/spec/19d-client-backend-compat.md):
  // below the live level → a forced update (boot window / unattended page) or
  // the non-dismissable modal. One instance, here at the root, so it covers
  // every route; the gym id only feeds the GA4 `client_forced_update` event.
  const { gymId: activeGymId } = useGym();
  const compat = useClientCompat({ gymId: activeGymId });
  // GA4 `chunk_recovery`: the stale-chunk recovery reloaded this tab and the app
  // booted again (docs/spec/01c-sw-update-flow.md, "Telemetry"). Same root, same
  // gym id source as the forced-update report above.
  useChunkRecoveryReport({ gymId: activeGymId });
  // The update trace: a slow banner tap / pull-to-refresh, or an update that
  // landed on the same build, becomes one crashLogs row with the old page's
  // step-by-step record (docs/spec/01d-update-trace.md).
  useUpdateTraceReport();

  // Record PWA scope on first load — gym PWA starts on /gym/*, sendly PWA on /
  // (plus the launch gym's slug, which the push token write needs).
  useEffect(() => {
    recordPwaLaunch(location.pathname);
  }, []);

  // PWA restore + persist. Subtle ordering and a gate ref keep these
  // from clobbering each other:
  //
  //  - In-app tab navigation goes through `window.history.pushState`
  //    (see `useMainApp.setTab` and `AdminPanel.setActiveSection`),
  //    which is silent: no `popstate`, no `hashchange`, and
  //    react-router's `useLocation` doesn't update. Both helpers
  //    therefore manually dispatch a `hashchange` after their
  //    `pushState`, and we listen to that event directly via a window
  //    listener — `useLocation` alone would miss those tab switches.
  //
  //  - The persist effect must NOT fire on the very first mount with a
  //    bare `/gym/{slug}` URL, because that would overwrite a saved
  //    `/gym/{slug}#admin/foo` (from the previous PWA session) before
  //    the restore effect gets a chance to read it. We gate persist on
  //    `restoreCheckedRef`, which the restore effect sets in a `finally`
  //    block — so by the time persist runs in the next render pass, the
  //    saved URL has already been navigated to (or determined to be
  //    irrelevant) and writing the new location is safe.
  const restoreCheckedRef = useRef(false);

  // PWA restore — declared FIRST so its `restoreCheckedRef = true` gate
  // is set before the persist effects fire on the same render. Triggers
  // in two cases:
  //   (a) Sendly PWA (start_url `/`) lands on `/` — the saved URL takes
  //       them straight to the last gym + tab.
  //   (b) Per-gym PWA (start_url `/gym/{slug}`) lands on the gym base
  //       with no hash — the saved URL adds the last tab hash back. We
  //       require the saved URL to share the current pathname so a
  //       per-gym PWA for gym A never silently redirects to gym B.
  //
  // sessionStorage guards against the redirect re-firing when the user
  // later navigates from a gym back to the selector (`/`) in the same
  // session — that's an intentional move, not a fresh launch.
  useEffect(() => {
    if (restoreCheckedRef.current) return;
    try {
      if (sessionStore.get("sendly-restored") === "1") {
        restoreCheckedRef.current = true;
        return;
      }
      const isStandalone = window.matchMedia("(display-mode: standalone)").matches
        || window.navigator.standalone === true;
      if (!isStandalone) {
        restoreCheckedRef.current = true;
        return;
      }
      const last = localStore.get("sendly-last-gym-url");
      if (!last || !last.startsWith("/gym/")) {
        restoreCheckedRef.current = true;
        return;
      }
      const cur = location.pathname + location.search + location.hash;
      if (cur === last) {
        sessionStore.set("sendly-restored", "1");
        restoreCheckedRef.current = true;
        return;
      }
      const atRoot = location.pathname === "/";
      const gymBaseMatch = location.pathname.match(/^\/gym\/[^/]+\/?$/);
      const atSameGymBase = !location.search && !location.hash
        && !!gymBaseMatch
        && last.startsWith(location.pathname.replace(/\/$/, ""));
      if (!atRoot && !atSameGymBase) {
        restoreCheckedRef.current = true;
        return;
      }
      sessionStore.set("sendly-restored", "1");
      restoreCheckedRef.current = true;
      navigate(last, { replace: true });
    } catch {
      restoreCheckedRef.current = true;
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  // Persist (react-router-driven changes) — initial mount + any
  // `navigate()` calls. Gated by `restoreCheckedRef` so the first fire
  // (with the bare PWA-launch URL) doesn't trash the saved value.
  //
  // Public spectator pages are excluded: they live under `/gym/...` too, and
  // in a standalone PWA the user reaches them by an in-app navigation (see
  // `useOpenPublicPage`). Saving one would make the NEXT cold launch restore
  // onto a competition dashboard instead of the app.
  useEffect(() => {
    if (!restoreCheckedRef.current) return;
    if (!location.pathname.startsWith("/gym/")) return;
    if (isPublicDashboard) return;
    localStore.set("sendly-last-gym-url", location.pathname + location.search + location.hash);
  }, [location.pathname, location.search, location.hash, isPublicDashboard]);

  // Persist (window event-driven changes) — in-app tab switches that go
  // through `pushState` + manual `hashchange` dispatch. Also gated.
  useEffect(() => {
    const persist = () => {
      if (!restoreCheckedRef.current) return;
      if (!window.location.pathname.startsWith("/gym/")) return;
      if (PUBLIC_DASHBOARD_RE.test(window.location.pathname)) return;
      localStore.set("sendly-last-gym-url", window.location.pathname + window.location.search + window.location.hash);
    };
    window.addEventListener("hashchange", persist);
    window.addEventListener("popstate", persist);
    return () => {
      window.removeEventListener("hashchange", persist);
      window.removeEventListener("popstate", persist);
    };
  }, []);

  // Clear the chunk-error recovery guards once the app has booted stably.
  // index.html's triggerStaleChunkRecovery keeps two sessionStorage guards:
  //  • `sendly-sw-purge`    — per-boot rate-limit. Survives the recovery
  //    reload; if it stayed set forever, a SECOND chunk failure later in the
  //    same tab (e.g. right after the user accepts the update banner) couldn't
  //    recover. Cleared after 5 s of stable operation so future failures can.
  //  • `sendly-swr-hardcap` — HARD loop-cap that the 5 s clear above must NOT
  //    reset: a lazy chunk that fails AFTER a healthy boot (e.g. one modal's
  //    chunk pinned as a poisoned `immutable` entry in the browser HTTP cache)
  //    makes every boot look "stable", so the 5 s clear would let the cache-bust
  //    reload fire forever. We clear it only after a LONGER 30 s healthy run —
  //    in a recovery loop the page reloads every few seconds (< 30 s) so this
  //    never fires and the cap holds (breaking the loop); a genuinely healthy
  //    session clears it so a much-later unrelated failure gets fresh attempts.
  useEffect(() => {
    const t1 = setTimeout(() => { sessionStore.remove("sendly-sw-purge"); }, 5000);
    const t2 = setTimeout(() => { sessionStore.remove("sendly-swr-hardcap"); }, 30000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // ── App-level legal consent check ──
  // Initialize from auth.currentUser so the onSnapshot starts immediately
  // on returning users (no gap between auth resolve and consent data load).
  // One users/{uid} listener for the whole app, from the provider in
  // index.js. The consent gate used to run its own — see that file for the
  // two guards it grew and the other three copies that lacked them.
  const { authUser: consentUser, userData: consentUserData, userDocLoaded: consentLoaded } = useCurrentUser();
  // The account's language, applied once per sign-in on EVERY route (a direct
  // load of /profile, /admin, a legal page …) — it must stay here, above the
  // route table, not in a per-surface hook.
  useAccountLanguageRestore();
  const [platformAccent, setPlatformAccent] = useState(DEFAULT_ACCENT);

  useEffect(() => {
    getPlatformConfig().then(cfg => {
      if (cfg.accentColor) setPlatformAccent(cfg.accentColor);
    }).catch(() => {});
  }, []);

  // Raw consent check — true if user needs to accept terms
  const rawNeedsConsent = useMemo(() => {
    if (!consentUser || !consentLoaded) return false;
    if (location.pathname.startsWith("/legal/")) return false;
    if (!consentUserData) return true;
    // Ordered check ("older than"), not !== — a stale cached bundle must
    // never re-prompt against a newer stored consent (and downgrade it on
    // accept). See src/utils/legalConsent.js.
    return isConsentOutdated(consentUserData.legalConsent);
  }, [consentUser, consentUserData, consentLoaded, location.pathname]);

  // Debounced consent — prevents brief flash on returning users where cached data
  // arrives one frame after auth resolves. Only show modal after 400ms stable "needs consent".
  const [needsConsent, setNeedsConsent] = useState(false);
  useEffect(() => {
    if (!rawNeedsConsent) { setNeedsConsent(false); return; }
    const timer = setTimeout(() => setNeedsConsent(true), 400);
    return () => clearTimeout(timer);
  }, [rawNeedsConsent]);


  // Platform-wide ban gate — a globally-banned user (server-only `platformBanned`
  // flag on their own doc) is blocked from every authenticated surface. Public /
  // kiosk dashboards, embeds, and legal pages stay reachable (they're public and
  // carry no personal session). The flag is server-written only (Firestore rules),
  // so the user cannot lift their own ban.
  const platformBanned = !!(consentUser && consentLoaded && consentUserData?.platformBanned === true);
  const showPlatformBan = platformBanned && !isPublicDashboard && !location.pathname.startsWith("/legal/");

  return (
    <ErrorBoundary resetKey={location.pathname}>
    <Suspense fallback={<LoadingBlank stage="suspense" />}>
    {/* The platform accent covers the app chrome as well as the routes: the
        toast, the update banner and the consent modals belong to the platform,
        not to whichever gym happens to be open behind them. A gym's own pages
        re-pin the accent in `GymShell`. */}
    <ThemeProvider accent={platformAccent}>
    <ToastProvider>
    {/* One top-anchored strip for every pinned top banner, so the update
        notice and the install invitation stack instead of covering each
        other. Wraps the routes too: <InstallBanner> lives inside MainApp. */}
    <TopBannerHost>
      <UpdateNotices compat={compat} />
      <OfflineBanner />
      {!isPublicDashboard && <CookieConsent />}
      {/* Each lazy overlay gets its own fallback-less boundary: a suspending
          child hands control to the NEAREST Suspense, and the root one's
          fallback is the full-page loader — the whole app (routes included)
          would blank while a modal's chunk downloads. */}
      {needsConsent && !showPlatformBan && (
        <Suspense fallback={null}>
          <ConsentModal authUser={consentUser} userData={consentUserData} />
        </Suspense>
      )}
      {/* Platform-moderation name-reset notice: non-blocking, shown once via the
          user-doc flag. Suppressed while the blocking consent modal is up. */}
      {!needsConsent && !showPlatformBan && consentUser && consentUserData?.nameResetNotice && (
        <Suspense fallback={null}>
          <NameResetNoticeModal authUser={consentUser} notice={consentUserData.nameResetNotice} />
        </Suspense>
      )}
      {showPlatformBan ? (
        <BannedScreen
          platform
          reason={consentUserData?.platformBanReason}
          bannedAt={consentUserData?.platformBannedAt}
          onLogout={() => { signOut(auth).catch(() => {}); }}
        />
      ) : (
      <Routes>
        <Route path="/" element={<GymSelector />} />
        <Route path="/calendar" element={<PlatformCalendarPage />} />
        <Route path="/admin" element={<PlatformAdminGuard />} />
        <Route path="/profile" element={<GlobalProfileGuard />} />
        <Route path="/gym/:gymSlug" element={<GymShell />}>
          <Route index element={<MainApp />} />
          <Route path="event/:eventId" element={<MainApp />} />
          <Route path="event/:eventId/register" element={<EventRegisterLanding />} />
          <Route path="dashboard" element={<TvDashboard />} />
          <Route path="competition/:compId" element={<CompetitionDashboard />} />
          <Route path="competition/:compId/register" element={<CompetitionRegisterLanding />} />
          <Route path="series/:seriesId" element={<SeriesDashboard />} />
        </Route>
        <Route path="/tv-control/:gymId/:screenId" element={<TvControl />} />
        <Route path="/embed/:slug/:widget" element={<PublicWidget />} />
        <Route path="/version" element={<VersionPage />} />
        <Route path="/wrapped/:year" element={<WrappedPage />} />
        <Route path="/legal/:docType" element={<LegalPage />} />
        {/* Sendly MCP server add-on consent page — outside gym context, no
            gymId in the route: the OAuth client asks for whichever gyms the
            signed-in user administers, not one specific gym. See
            docs/spec/20-mcp-server.md "Consent page". Not a Hosting
            rewrite (unlike /mcp, /oauth/token, /oauth/revoke) — this is a
            plain SPA route served by the catch-all below. */}
        <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
        {/* Catch-all: any unknown path renders a friendly "not found" page
            instead of a blank screen. Avoids the white-screen-of-death when a
            stale QR code or typo in a deep link doesn't match any route. */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      )}
    </TopBannerHost>
    </ToastProvider>
    </ThemeProvider>
    </Suspense>
    </ErrorBoundary>
  );
}
